import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import { baseUrl, Fetch } from "../../../shared/Fetch";

interface Props {
  visible: boolean;
  tenderId: number | null;
  toast: any;
  onHide: () => void;
}

interface HistoryItem {
  id: number;
  tenderId: number;
  action: string;
  changedBy: string;
  changedAt: string;
  old_Values: string;
  new_Values: string;
  current_File_Path: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  Title: "Title",
  Description: "Description",
  Status_Comment: "Status Comment",
  Tender_Doc_Path: "Document",
  Tender_Client: "Client",
  Created_By: "Created By",
  Status: "Status",
  Tender_Published_Date: "Published Date",
  Pre_Proposal_Meeting_Date: "Pre-Proposal Meeting Date",
  Q_A_Date: "Q&A Date",
  Due_Date: "Due Date",
  Assigned_To: "Assigned To",
  Assigned_By: "Assigned By",
  Workstream_Category: "Workstream Category",
  Deadline_Date: "Deadline Date",
  Proposal_Assigned_To: "Proposal Assigned To",
  Proposal_Assigned_By: "Proposal Assigned By",
  Proposal_Assigned_At: "Proposal Assigned At",
  RFP_Number:"RFP Number"
};

const SKIP_FIELDS: Record<string, true> = {
  Created_By_ID: true,
  Assigned_To_ID: true,
  Assigned_By_ID: true,
};

type ActionType = "created" | "updated" | "assigned" | "re-Assigned" | "status updated" | "workstream created" | "other";

interface ActionMeta {
  type: ActionType;
  icon: string;
  color: string;
  bg: string;
  border: string;
  label: string;
}

function parseJson(str: string): Record<string, any> {
  try {
    return JSON.parse(str);
  } catch {
    return {};
  }
}

function getDiff(oldObj: Record<string, any>, newObj: Record<string, any>) {
  const allKeys = Object.keys(oldObj).concat(Object.keys(newObj).filter((k) => !(k in oldObj)));
  const changes: { key: string; label: string; old: string; new: string }[] = [];

  for (const k of allKeys) {
    if (SKIP_FIELDS[k]) continue;
    const o = oldObj[k];
    const n = newObj[k];
    if (o === n) continue;
    const oStr = o === null || o === undefined ? "—" : String(o);
    const nStr = n === null || n === undefined ? "—" : String(n);
    if (oStr === nStr || (oStr === "null" && nStr === "null")) continue;
    changes.push({
      key: k,
      label: FIELD_LABELS[k] ?? k.replace(/_/g, " "),
      old: formatValue(k, o),
      new: formatValue(k, n),
    });
  }
  return changes;
}

function getActionMeta(action: string): ActionMeta {
  const a = action.toLowerCase();
  const isBidAssgn = a.includes("Created And Bid Manager is Auto Assigned".toLowerCase());
  const isProposalAddgn = a.includes("Status Updated And Proposal Writer".toLowerCase());
  if (a.includes("workstream")) return { type: "workstream created", icon: "pi pi-sitemap", color: "#185FA5", bg: "#E6F1FB", border: "#B5D4F4", label: "Workstream Created" };
  if (a.includes("created")) return { type: "created", icon: "pi-user-edit", color: "#185FA5", bg: "#E6F1FB", border: "#B5D4F4", label: isBidAssgn ? action : "Created" };
  if (a.includes("status")) return { type: "status updated", icon: "pi-tag", color: "#6D3B11", bg: "#FDF3E3", border: "#F5CBA7", label: isProposalAddgn ? action : "Status Updated" };
  if (a.includes("updated")) return { type: "updated", icon: "pi-file-edit", color: "#3B6D11", bg: "#EAF3DE", border: "#C0DD97", label: "Updated" };
  if (a.includes("re")) return { type: "re-Assigned", icon: "pi-sync", color: "#185FA5", bg: "#E6F1FB", border: "#B5D4F4", label: "Re-Assigned" };
  if (a.includes("assigned")) return { type: "assigned", icon: "pi-user-plus", color: "#185FA5", bg: "#E6F1FB", border: "#B5D4F4", label: "Assigned" };
  return { type: "other", icon: "pi-history", color: "#5F5E5A", bg: "#F1EFE8", border: "#D3D1C7", label: action };
}
//-----------------Date Field------------------------//

function formatDt(str: string) {
  if (!str) return "";
  const d = new Date(str);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const DATE_FIELDS = ["Tender_Published_Date", "Pre_Proposal_Meeting_Date", "Q_A_Date", "Due_Date", "Deadline_Date", "Assigned_At", "Proposal_Assigned_At", "ChangedAt"];

const formatValue = (key: string, value: any) => {
  if (value === null || value === undefined || value === "") return "—";

  if (DATE_FIELDS.includes(key)) return formatDt(value);

  return value;
};

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  dialog: { width: "56vw", minWidth: 480, maxWidth: 780, borderRadius: 16 },
  container: { padding: "8px 4px 4px", maxHeight: "72vh", overflowY: "auto" as const, scrollbarWidth: "thin" as const },
  emptyState: { display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 12, padding: "3rem 1rem", color: "var(--text-color-secondary)" },
  timelineRow: { display: "flex", gap: 14, position: "relative" as const },
  spineWrap: { display: "flex", flexDirection: "column" as const, alignItems: "center", width: 36, flexShrink: 0, paddingTop: 2 },
  card: { background: "var(--surface-card)", border: "1px solid var(--surface-border)", borderRadius: 12, padding: "14px 16px", flex: 1, marginBottom: 20 },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap" as const, gap: 8 },
  fieldRow: { display: "grid", gridTemplateColumns: "160px auto 1fr", alignItems: "center", gap: 8, fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--surface-border)" },
  chip: (color: string, bg: string) => ({ fontSize: 11, fontWeight: 600, color, background: bg, padding: "2px 10px", borderRadius: 20, letterSpacing: "0.03em", textTransform: "uppercase" as const, whiteSpace: "nowrap" as const }),
  valueNew: { color: "var(--text-color)", background: "var(--surface-200)", padding: "2px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 },
  valueOld: { color: "var(--text-color-secondary)", fontSize: 12 },
  avatar: (color: string, bg: string) => ({ width: 36, height: 36, borderRadius: "50%", background: bg, color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, border: `2px solid ${color}33` }),
  docBtn: (primary?: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 12px",
    borderRadius: 6,
    border: primary ? "1.5px solid #185FA5" : "1.5px solid var(--surface-border)",
    color: primary ? "#185FA5" : "var(--text-color-secondary)",
    background: "transparent",
    cursor: "pointer",
    transition: "background 0.15s",
  }),
};

// ─── Component ────────────────────────────────────────────────────────────────

const TenderMastHistory = ({ visible, onHide, toast, tenderId }: Props) => {
  const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  // ─── Data loading ──────────────────────────────────────────────────────────

  const loadHistory = async () => {
    if (!tenderId) return;
    setLoading(true);
    try {
      const response = await Fetch("Tender_Mast/history", "GET", null, `tenderId=${tenderId}`, toast);
      if (response?.ok) setHistoryList(await response.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && tenderId) loadHistory();
    if (!visible) setHistoryList([]);
  }, [visible, tenderId]);

  // ─── Download helpers ──────────────────────────────────────────────────────

  const downloadDoc = async (tenderID: number, key: string) => {
    if (downloading) return;
    setDownloading(key);
    try {
      const response = await fetch(`${baseUrl}Tender_Mast/download?tenderID=${tenderID}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("auth") + "")?.token}` },
      });
      if (response.status === 200) {
        const blob = await response.blob();
        const disposition = response.headers.get("content-disposition");
        let fileName = "Tender_Document";
        if (disposition) {
          const m = disposition.match(/filename="?([^"]+)"?/);
          if (m?.[1]) fileName = m[1];
        }
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
      } else if (response.status === 400) {
        toast.current?.show({ severity: "error", detail: await response.text(), life: 3000 });
      }
    } catch {
      toast.current?.show({ severity: "error", detail: "Download failed", life: 3000 });
    } finally {
      setDownloading(null);
    }
  };

  // const downloadByPath = async (filePath: string, key: string) => {
  //   if (downloading) return;
  //   setDownloading(key);
  //   try {
  //     const response = await fetch(`${baseUrl}Tender_Mast/download-snapshot?filePath=${encodeURIComponent(filePath)}`, {
  //       method: "GET",
  //       headers: { Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("auth") + "")?.token}` },
  //     });
  //     if (response.status === 200) {
  //       const blob = await response.blob();
  //       const disposition = response.headers.get("content-disposition");
  //       let fileName = "Tender_Document_Snapshot";
  //       if (disposition) {
  //         const m = disposition.match(/filename="?([^"]+)"?/);
  //         if (m?.[1]) fileName = m[1];
  //       }
  //       const href = URL.createObjectURL(blob);
  //       const a = document.createElement("a");
  //       a.href = href;
  //       a.download = fileName;
  //       document.body.appendChild(a);
  //       a.click();
  //       document.body.removeChild(a);
  //       URL.revokeObjectURL(href);
  //     } else {
  //       toast.current?.show({ severity: "error", detail: "File not found", life: 3000 });
  //     }
  //   } catch {
  //     toast.current?.show({ severity: "error", detail: "Download failed", life: 3000 });
  //   } finally {
  //     setDownloading(null);
  //   }
  // };

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderDocButton = (item: HistoryItem) => {
    if (!item.current_File_Path) return null;
    const key = `dl-${item.id}`;
    const isLoading = downloading === key;
    return (
      <button style={styles.docBtn(true)} onClick={() => downloadDoc(item.tenderId, key)} disabled={!!downloading}>
        <i className={`pi ${isLoading ? "pi-spin pi-spinner" : "pi-download"}`} style={{ fontSize: 12 }} />
        Download file
      </button>
    );
  };

  const renderCardHeader = (item: HistoryItem, meta: ActionMeta) => {
    const initials = getInitials(item.changedBy || "?");
    return (
      <>
        <div style={styles.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={styles.avatar(meta.color, meta.bg)}>{initials}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)", lineHeight: 1.3 }}>{item.changedBy}</div>
              <div style={{ fontSize: 11, color: "var(--text-color-secondary)" }}>{formatDt(item.changedAt)}</div>
            </div>
          </div>
          <span style={styles.chip(meta.color, meta.bg)}>{meta.label}</span>
        </div>
        <div style={{ height: 1, background: "var(--surface-border)", marginBottom: 12, opacity: 0.6 }} />
      </>
    );
  };

  const renderSpine = (meta: ActionMeta, isLast: boolean, icon?: string) => (
    <div style={styles.spineWrap}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: meta.bg, border: `1.5px solid ${meta.border}`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, flexShrink: 0 }}>
        <i className={`pi ${icon ?? meta.icon}`} style={{ color: meta.color, fontSize: 14 }} />
      </div>
      {!isLast && <div style={{ width: 1.5, flex: 1, minHeight: 24, background: "var(--surface-border)", margin: "3px 0", opacity: 0.7 }} />}
    </div>
  );

  // Created
  const renderCreated = (item: HistoryItem, newObj: Record<string, any>) => {
    const visibleFields = Object.entries(newObj).filter(([k]) => !SKIP_FIELDS[k] && FIELD_LABELS[k] && k !== "Tender_Doc_Path");
    return (
      <>
        {visibleFields.map(([k, v]) => (
          <div key={k} style={{ ...styles.fieldRow, borderBottom: "none", padding: "3px 0" }}>
            <span style={{ fontSize: 12, color: "var(--text-color-secondary)" }}>{FIELD_LABELS[k]}</span>
            <span />
            <span style={styles.valueNew}>{formatValue(k, v)}</span>
          </div>
        ))}
        {item.current_File_Path && (
          <div style={{ ...styles.fieldRow, borderBottom: "none", padding: "3px 0" }}>
            <span style={{ fontSize: 12, color: "var(--text-color-secondary)" }}>File</span>
            <span />
            {renderDocButton(item)}
          </div>
        )}
      </>
    );
  };

  // Status Updated
  const renderStatusUpdate = (oldObj: Record<string, any>, newObj: Record<string, any>) => (
    <>
      <div style={{ ...styles.fieldRow, borderBottom: "none" }}>
        <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Status</span>
        <span />
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={styles.valueOld}>{oldObj.Status ?? "—"}</span>
          <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
          <span style={styles.valueNew}>{newObj.Status ?? "—"}</span>
        </span>
      </div>
      <div style={{ ...styles.fieldRow, borderBottom: "none" }}>
        <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Comment</span>
        <span />
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={styles.valueOld}>{oldObj.Status_Comment ?? "—"}</span>
          <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
          <span style={styles.valueNew}>{newObj.Status_Comment ?? "—"}</span>
        </span>
      </div>
      {newObj.Assigned_To && (
        <div style={{ ...styles.fieldRow, borderBottom: "none" }}>
          <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Assigned To</span>
          <span />
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={styles.valueOld}>{oldObj.Assigned_To ?? "—"}</span>
            <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
            <span style={styles.valueNew}>{newObj.Assigned_To ?? "—"}</span>
          </span>
        </div>
      )}
      {newObj.Assigned_To && (
        <div style={{ ...styles.fieldRow, borderBottom: "none" }}>
          <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Assigned At</span>
          <span />
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={styles.valueOld}>{formatValue("Assigned_At", oldObj.Assigned_At)}</span>
            <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
            <span style={styles.valueNew}>{formatValue("Assigned_At", newObj.Assigned_At)}</span>
          </span>
        </div>
      )}
    </>
  );

  // Tender Updated (field diff)
  const renderUpdated = (item: HistoryItem, changes: ReturnType<typeof getDiff>) => {
    const docChange = changes.find((c) => c.key === "Tender_Doc_Path");
    const otherChanges = changes.filter((c) => c.key !== "Tender_Doc_Path");
    return (
      <>
        {otherChanges.length === 0 && !docChange && <div style={{ fontSize: 12, color: "var(--text-color-secondary)", fontStyle: "italic" }}>No field changes recorded.</div>}
        {otherChanges.map((c, i) => (
          <div key={i} style={{ ...styles.fieldRow, borderBottom: i < otherChanges.length - 1 || docChange ? "1px solid var(--surface-border)" : "none" }}>
            <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>{c.label}</span>
            <span />
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={styles.valueOld}>{c.old}</span>
              <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
              <span style={styles.valueNew}>{c.new}</span>
            </span>
          </div>
        ))}
        {docChange && (
          <div style={{ ...styles.fieldRow, borderBottom: "none", padding: "3px 0" }}>
            <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>File Updated</span>
            <span />
            {renderDocButton(item)}
          </div>
        )}
      </>
    );
  };

  // Assigned / Re-Assigned
  const renderAssigned = (meta: ActionMeta, oldObj: Record<string, any>, newObj: Record<string, any>) => (
    <>
      <div style={{ ...styles.fieldRow, borderBottom: "1px solid var(--surface-border)" }}>
        <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Assigned To</span>
        <span />
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600 }}>
          {meta.type === "re-Assigned" && (
            <>
              <span style={styles.valueOld}>{oldObj.Assigned_To || "—"}</span>
              <i className="pi pi-arrow-right" style={{ fontSize: 8 }} />
            </>
          )}
          <span style={styles.valueNew}>{newObj.Assigned_To ?? "—"}</span>
        </span>
      </div>
      <div style={{ ...styles.fieldRow, borderBottom: "none" }}>
        <span style={{ fontSize: 12, color: "var(--text-color-secondary)", fontWeight: 500 }}>Assigned By</span>
        <span />
        <span style={styles.valueNew}>{newObj.Assigned_By ?? "—"}</span>
      </div>
    </>
  );

  // ─── Main render ───────────────────────────────────────────────────────────

  const headerStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 15,
    letterSpacing: "-0.01em",
    color: "var(--text-color)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  return (
    <Dialog
      visible={visible}
      header={
        <div style={headerStyle}>
          <i className="pi pi-clock" style={{ color: "var(--primary-color)", fontSize: 16 }} />
          RFP History
        </div>
      }
      style={styles.dialog}
      modal
      onHide={onHide}
      draggable={false}
      resizable={false}
    >
      <div style={styles.container}>
        {/* Loading */}
        {loading && (
          <div style={styles.emptyState}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, color: "var(--primary-color)" }} />
            <span style={{ fontSize: 13 }}>Loading history…</span>
          </div>
        )}

        {/* Empty */}
        {!loading && !historyList.length && (
          <div style={styles.emptyState}>
            <i className="pi pi-inbox" style={{ fontSize: 32, opacity: 0.35 }} />
            <span style={{ fontSize: 14 }}>No history found for this RFP.</span>
          </div>
        )}

        {/* Timeline */}
        {!loading &&
          historyList.map((item, idx) => {
            const oldObj = parseJson(item.old_Values);
            const newObj = parseJson(item.new_Values);
            const meta = getActionMeta(item.action);
            const isLast = idx === historyList.length - 1;
            const isCreated = meta.type === "created" || meta.type === "workstream created";
            const changes = isCreated ? [] : getDiff(oldObj, newObj);

            // ── Assigned / Re-Assigned ──────────────────────────────────────
            if (meta.type === "assigned" || meta.type === "re-Assigned") {
              return (
                <div key={item.id} style={styles.timelineRow}>
                  {renderSpine(meta, isLast, meta.type === "re-Assigned" ? "pi-sync" : "pi-user-plus")}
                  <div style={styles.card}>
                    {renderCardHeader(item, meta)}
                    {renderAssigned(meta, oldObj, newObj)}
                  </div>
                </div>
              );
            }

            // ── All other actions (Created / Status Updated / Updated) ──────
            return (
              <div key={item.id} style={styles.timelineRow}>
                {renderSpine(meta, isLast)}
                <div style={styles.card}>
                  {renderCardHeader(item, meta)}
                  {isCreated ? renderCreated(item, newObj) : meta.type === "status updated" ? renderStatusUpdate(oldObj, newObj) : renderUpdated(item, changes)}
                </div>
              </div>
            );
          })}
      </div>
    </Dialog>
  );
};

export default TenderMastHistory;
